// biome-ignore-all lint/correctness/useUniqueElementIds: Script tag requires stable ID
import { splitEncoding } from '@contentful/live-preview';
import { GoogleTagManager } from '@next/third-parties/google';
import Cookies from 'js-cookie';
import Head from 'next/head';
import Script from 'next/script';
import { Fragment, type ReactElement, useEffect, useState } from 'react';

import type { Seo } from '@/contentful/contentful-exports';
import {
  abtastyCodeFlagWidgetHandler,
  abtastyScript,
} from '@/utils/abtasty/abtasty';
import {
  CAPITAL_ONE_SHOPPING_SDK_URL,
  capitalOneShoppingStubScript,
} from '@/utils/capitalOneShopping/capitalOneShopping';
import { cloudfrontGeoScript } from '@/utils/cloudfrontGeoScript';
import { GTM_ID, GTM_SCRIPT_URL } from '@/utils/constants/google';
import getEnv, { Environment } from '@/utils/environment';
import { setImgWidth } from '@/utils/frontify';
import { fullStoryScript } from '@/utils/fullstory/fullstory';
import { LeadiDscript } from '@/utils/zeusClient/leadIdScript';
import { TrustedFormScript } from '@/utils/zeusClient/trustedFormScript';

interface Props {
  seoFields?: Seo;
  currentPath: string;
  shouldLoadAbTasty: boolean;
  ogType?: 'website' | 'article';
}

export default function HTMLHead({
  seoFields,
  currentPath,
  shouldLoadAbTasty,
  ogType = 'website',
}: Props): ReactElement {
  const {
    enableSearchEngineIndexing,
    metaText,
    metaTitle,
    metaKeywords,
    previewImage,
    canonicalUrl,
  } = seoFields?.fields || {};

  const [shouldLoadStatisticsScripts, setShouldLoadStatisticsScripts] =
    useState(false);

  const baseUrl = 'https://www.sunrun.com';
  const safePath = currentPath ? currentPath.replace(/\/$/, '') : '';

  const getDefaultCanonicalUrl = (): string => {
    return `${baseUrl}${safePath}`;
  };

  const resolvedCanonicalUrl = canonicalUrl || getDefaultCanonicalUrl();

  useEffect(() => {
    const interval = setInterval(() => {
      const cookies = Cookies.get();

      if (
        !shouldLoadStatisticsScripts &&
        cookies.sunrun_allow_analytics_storage === 'true'
      ) {
        setShouldLoadStatisticsScripts(true);
      }
    }, 5 * 1000);

    return () => clearInterval(interval);
  });

  return (
    <>
      <Head>
        <title>{splitEncoding(metaTitle || 'Sunrun').cleaned}</title>
        <link
          rel='icon'
          type='image/x-icon'
          href='https://www.sunrun.com/sites/default/themes/sunrun/favicon.ico'
        />
        <link rel='canonical' href={resolvedCanonicalUrl} />
        <link
          rel='preload'
          href='/utils/geo/index.html'
          as='fetch'
          crossOrigin='anonymous'
          blocking='render'
          fetchPriority='high'
        />

        <meta property='og:site_name' content='Sunrun' />
        <meta property='og:type' content={ogType} />
        <meta property='og:url' content={resolvedCanonicalUrl} />
        {metaTitle && (
          <meta
            property='og:title'
            content={splitEncoding(metaTitle).cleaned}
          />
        )}
        {metaText && (
          <meta
            property='og:description'
            content={splitEncoding(metaText).cleaned}
          />
        )}

        <meta name='twitter:card' content='summary_large_image' />
        <meta name='twitter:domain' content='sunrun.com' />
        <meta name='twitter:url' content={resolvedCanonicalUrl} />
        {metaTitle && (
          <meta
            property='twitter:title'
            content={splitEncoding(metaTitle).cleaned}
          />
        )}
        {metaText && (
          <>
            <meta
              name='description'
              content={splitEncoding(metaText).cleaned}
            />
            <meta
              name='twitter:description'
              content={splitEncoding(metaText).cleaned}
            />
          </>
        )}
        {metaKeywords && (
          <meta
            name='keywords'
            content={metaKeywords
              .map((keyword) => splitEncoding(keyword).cleaned)
              .join(',')}
          />
        )}
        {previewImage && (
          <>
            <meta
              property='og:image'
              content={setImgWidth(previewImage[0].preview_url, 1440)}
            />
            <meta
              name='twitter:image'
              content={setImgWidth(previewImage[0].preview_url, 1200)}
            />
          </>
        )}
        <meta name='viewport' content='initial-scale=1.0, width=device-width' />

        {(getEnv() !== Environment.PROD || !enableSearchEngineIndexing) && (
          <meta name='robots' content='noindex,nofollow' />
        )}
      </Head>

      {shouldLoadStatisticsScripts && (
        <Fragment>
          <Script
            id='disable-fullstory-capture-on-startup'
            strategy='beforeInteractive'
          >
            {`window['_fs_capture_on_startup'] = false;`}
          </Script>
          <Script id='fullstory-inline' strategy='afterInteractive'>
            {fullStoryScript}
          </Script>
        </Fragment>
      )}

      {shouldLoadAbTasty && (
        <Fragment>
          <Script
            id='abtasty-code-flag-widget-handler'
            strategy='beforeInteractive'
          >
            {abtastyCodeFlagWidgetHandler}
          </Script>
          <Script id='abtasty-inline' strategy='beforeInteractive'>
            {abtastyScript}
          </Script>
        </Fragment>
      )}

      <Script id='cloudfrontGeoScript' strategy='beforeInteractive'>
        {cloudfrontGeoScript}
      </Script>

      <Script
        id='LeadiDscript'
        type='text/javascript'
        strategy='beforeInteractive'
      >
        {LeadiDscript}
      </Script>

      <Script
        id='TrustedFormScript'
        type='text/javascript'
        strategy='beforeInteractive'
      >
        {TrustedFormScript}
      </Script>

      <Script id='capital-one-shopping-stub' strategy='beforeInteractive'>
        {capitalOneShoppingStubScript}
      </Script>
      <Script
        id='capital-one-shopping-sdk'
        src={CAPITAL_ONE_SHOPPING_SDK_URL}
        strategy='afterInteractive'
      />
      <GoogleTagManager gtmId={GTM_ID} gtmScriptUrl={GTM_SCRIPT_URL} />
    </>
  );
}
